package org.gstechnovos.jexonplastech.sections.products.sections

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.gstechnovos.jexonplastech.sections.products.styles.ProductsHeaderContainerStyle
import org.gstechnovos.jexonplastech.theme.toSitePalette
import org.gstechnovos.jexonplastech.util.LocalBreakpoint
import org.gstechnovos.jexonplastech.util.Res
import org.gstechnovos.jexonplastech.util.animateSlideUp
import org.gstechnovos.jexonplastech.util.rememberScrollVisibility
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

// ==========================================
// CMS DATA MODEL
// ==========================================
/**
 *
 * Text Constraints:
 *
 * Headline: Renders at a large 50px on desktop and 36px on mobile.
 * Limit to 2–5 words (e.g., "Product Catalog") to prevent it from stacking into too many lines
 * and pushing the catalog down.
 *
 * Description: Renders at 20px on desktop and 16px on mobile.
 * Limit to 2–3 sentences (approx. 15–25 words) to maintain a clean, readable introduction.
 *
 */
data class ProductsHeaderData(
    val headline: String,
    val description: String
)

val defaultProductsHeaderData = ProductsHeaderData(
    headline = "Product Catalog",
    description = "Explore our complete range of high-quality, durable plastic homeware and cleaning solutions designed for global B2B distribution."
)

// ==========================================
// COMPONENT
// ==========================================
@Composable
fun ProductsHeaderSection(data: ProductsHeaderData = defaultProductsHeaderData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()

    val (isVisible, visibilityRef) = rememberScrollVisibility(threshold = 0.1)

    Column(
        modifier = ProductsHeaderContainerStyle.toModifier().padding(
            topBottom = if (isDesktop) 100.px else 60.px, leftRight = 5.percent
        ),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Div(
            attrs = Modifier
                .fillMaxWidth()
                .display(DisplayStyle.Flex)
                .flexDirection(FlexDirection.Column)
                .alignItems(com.varabyte.kobweb.compose.css.AlignItems.Center)
                .toAttrs {
                    ref { element ->
                        visibilityRef(element)
                        onDispose {}
                    }
                }
        ) {
            Column(
                modifier = Modifier.maxWidth(800.px).animateSlideUp(isVisible, delayMs = 100),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.Center
            ) {
                // Main Headline
                SpanText(
                    text = data.headline,
                    modifier = Modifier
                        .color(palette.custom.white)
                        .fontSize(if (isDesktop) 50.px else 36.px)
                        .fontWeight(FontWeight.Bold)
                        .margin(bottom = 20.px)
                        .textAlign(TextAlign.Center)
                )

                // Introductory Text
                SpanText(
                    text = data.description,
                    modifier = Modifier
                        .color(palette.custom.white.toRgb().copyf(alpha = 0.85f))
                        .fontSize(if (isDesktop) 20.px else 16.px)
                        .lineHeight(1.6)
                        .textAlign(TextAlign.Center)
                )
            }
        }
    }
}